import {
  createFileRoute,
  useLocation,
  useNavigate,
} from "@tanstack/react-router";
export const Route = createFileRoute("/user/_layout/crm/accounts")({
  component: AccountsPage,
});

import { useState, useEffect, useMemo } from "react";
import instance from "@/lib/axios";
import { getCrmUrl } from "@/lib/crm";

import { SidebarInset } from "@/components/ui/sidebar";
import { DashboardHeader } from "@/components/dashboard-header";
import PageHeader from "@/components/reusable-components/header/page-header";
import FiltersPanel from "@/components/reusable-components/header/filter-pannel";
import { PaginationComponent } from "@/components/crud/Pagination/Pagination";
import { PAGE_SIZE_OPTIONS } from "@/lib/constants";
import { usePaginationState } from "@/hooks/ui/usePaginationState";
import { DeleteConfirmDialog } from "@/components/crud/DeleteConfirmDialog/DeleteConfirmDialog";
import { AccountTable } from "@/components/crm/tables/AccountTable";
import { AccountFormSheet } from "@/components/crm/forms/AccountFormSheet";
import {
  useFetchAccounts,
  useAccountMutations,
  type AccountOrdering,
} from "@/hooks/api/crm/useAccounts";
import SearchFilter from "@/components/reusable-components/header/search-filter";
import { AssigneeFilter } from "@/components/crm/AssigneeFilter";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { useDebounce } from "@/hooks/use-debounce";
import {
  useDataTableOrdering,
  type SortDirection,
} from "@/hooks/ui/useDataTableOrdering";
import type { CrmAccount, CrmAccountPayload } from "@/types/crm";
import { ACCOUNT_TYPES } from "@/types/crm";

function AccountsPage() {
  const { page, setPage, pageSize, handlePageSizeChange } =
    usePaginationState();

  const { ordering, toggleOrdering, getApiOrdering, setOrdering } =
    useDataTableOrdering<
      {
        name: SortDirection;
        type: SortDirection;
        industry: SortDirection;
        email: SortDirection;
        assigned_to__first_name: SortDirection;
      },
      AccountOrdering
    >({
      name: null,
      type: null,
      industry: null,
      email: null,
      assigned_to__first_name: null,
    });

  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, resetDebouncedSearch] = useDebounce(searchQuery, 500);
  const [typeFilter, setTypeFilter] = useState("");
  // Undefined = all team-visible records; pick a user to narrow.
  const [assignedTo, setAssignedTo] = useState<number | undefined>(
    undefined
  );
  const [showFilters, setShowFilters] = useState(false);

  const location = useLocation();
  const viewId = new URLSearchParams(location.searchStr).get("view") ?? undefined;
  const navigate = useNavigate();

  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [editingAccount, setEditingAccount] = useState<CrmAccount | null>(null);
  const [isViewOnly, setIsViewOnly] = useState(false);
  const [accountToDelete, setAccountToDelete] = useState<CrmAccount | null>(
    null
  );
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const accountsQuery = useFetchAccounts({
    page,
    page_size: pageSize,
    search: debouncedSearch || undefined,
    type: typeFilter || undefined,
    assigned_to: assignedTo ? String(assignedTo) : undefined,
    ordering: getApiOrdering(),
  });
  const accounts = accountsQuery.data?.results ?? [];
  const count = accountsQuery.data?.count ?? 0;

  const { createAccount, updateAccount, deleteAccount } =
    useAccountMutations();
  const isMutating =
    createAccount.isPending || updateAccount.isPending || deleteAccount.isPending;
  const isLoading = accountsQuery.isPending || isMutating;
  const isFetching = accountsQuery.isFetching || isMutating;

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil((count ?? 0) / pageSize)),
    [count, pageSize]
  );

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, typeFilter, assignedTo, ordering, setPage]);

  useEffect(() => {
    if (!viewId) return;
    instance
      .get<CrmAccount>(getCrmUrl(`accounts/${viewId}/`))
      .then((res) => {
        setEditingAccount(res.data);
        setIsViewOnly(true);
        setIsSheetOpen(true);
      })
      .catch(() =>
        navigate({ to: "/user/crm/accounts", search: {} })
      );
  }, [viewId]);

  const handleCloseSheet = () => {
    setIsSheetOpen(false);
    if (viewId)
      navigate({ to: "/user/crm/accounts", search: {} });
  };

  const resetFilters = () => {
    setSearchQuery("");
    resetDebouncedSearch();
    setTypeFilter("");
    setAssignedTo(undefined);
    setOrdering({
      name: null,
      type: null,
      industry: null,
      email: null,
      assigned_to__first_name: null,
    });
    setPage(1);
  };

  const handlePageChange = (p: number) => {
    if (p >= 1 && p <= totalPages) {
      setPage(p);
    }
  };

  const onCreate = async (data: CrmAccountPayload) => {
    await createAccount.mutateAsync(data);
    toast.success("Account created successfully!");
    setIsSheetOpen(false);
  };

  const onEdit = async (id: string, data: Partial<CrmAccountPayload>) => {
    await updateAccount.mutateAsync({ id, payload: data });
    toast.success("Account updated successfully!");
    setIsSheetOpen(false);
  };

  const handleDeleteConfirm = async () => {
    if (!accountToDelete) return;
    try {
      await deleteAccount.mutateAsync({ id: accountToDelete.id });
      toast.success("Account deleted successfully!");
      setIsDeleteDialogOpen(false);
      setAccountToDelete(null);
    } catch {
      // error toast comes from the mutation's meta.errorMessage
    }
  };

  return (
    <SidebarInset>
      <DashboardHeader />
      <div className="space-y-4 p-6 pt-0">
        <PageHeader
          title="Accounts"
          description="Manage your CRM accounts."
          onToggleFilter={() => setShowFilters((p) => !p)}
          onAdd={() => {
            setEditingAccount(null);
            setIsViewOnly(false);
            setIsSheetOpen(true);
          }}
          addLabel="Add Account"
          isLoading={isFetching}
        />

        <FiltersPanel
          show={showFilters}
          onClear={resetFilters}
          isLoading={isFetching}
        >
          <SearchFilter
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Search accounts..."
          />
          <Select
            value={typeFilter}
            onValueChange={(v) => setTypeFilter(v === "all" ? "" : v)}
            disabled={isFetching}
          >
            <SelectTrigger className="w-44">
              <SelectValue placeholder="All Types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              {ACCOUNT_TYPES.map((t) => (
                <SelectItem key={t} value={t}>
                  {t}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <AssigneeFilter value={assignedTo} onChange={setAssignedTo} />
        </FiltersPanel>

        <AccountTable
          accounts={accounts}
          onView={(a) => {
            navigate({
              to: "/user/crm/accounts/$id",
              params: { id: String(a.id) },
            });
          }}
          onEdit={(a) => {
            setEditingAccount(a);
            setIsViewOnly(false);
            setIsSheetOpen(true);
          }}
          onDelete={(a) => {
            setAccountToDelete(a);
            setIsDeleteDialogOpen(true);
          }}
          isLoading={isLoading}
          ordering={ordering}
          onOrderingChange={toggleOrdering}
        />

        <PaginationComponent
          count={count}
          page={page}
          onPageChange={handlePageChange}
          isLoading={isFetching}
          pageSize={pageSize}
          onPageSizeChange={handlePageSizeChange}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
        />

        <AccountFormSheet
          isOpen={isSheetOpen}
          isViewOnly={isViewOnly}
          onClose={handleCloseSheet}
          onCreate={onCreate}
          onEdit={onEdit}
          editingAccount={editingAccount}
          isLoading={isMutating}
        />

        <DeleteConfirmDialog
          isOpen={isDeleteDialogOpen}
          onClose={() => setIsDeleteDialogOpen(false)}
          onConfirm={handleDeleteConfirm}
          title="Delete Account"
          description={`Are you sure you want to delete "${accountToDelete?.name}"? This action cannot be undone.`}
          itemName={accountToDelete?.name}
          isLoading={deleteAccount.isPending}
          destructive
        />
      </div>
    </SidebarInset>
  );
}
